import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { LEGAL_DOCUMENTS, type LegalPolicyId } from '../../legal/documents';
import './BrowserSupportIndicator.css';

interface BrowserInfo {
  name: string;
  version: number;
  isSupported: boolean;
  isMacOS: boolean;
  isEdgeMacOS: boolean;
}

// Set to true to always show unsupported pill upfront
const ALWAYS_SHOW_UNSUPPORTED = true; // Set to false for real browser detection

// Minimum supported versions
const SUPPORTED_BROWSERS: Record<string, number> = {
  Chrome: 90,
  Edge: 90,
  Firefox: 90,
  Safari: 14,
};

const detectBrowser = (): BrowserInfo => {
  const userAgent = navigator.userAgent;
  let name = 'Unknown';
  let version = 0;

  // Detect macOS
  const isMacOS = userAgent.includes('Mac OS') || userAgent.includes('Macintosh');

  // Brave (Chromium-based, check before Chrome - use navigator.brave which exists only in Brave)
  const nav = navigator as Navigator & { brave?: unknown };
  if (userAgent.includes('Brave') || nav.brave) {
    name = 'Brave';
    const match = userAgent.match(/Chrome\/(\d+)/);
    version = match ? parseInt(match[1], 10) : 0;
  }
  // Edge (Chromium-based)
  else if (userAgent.includes('Edg/')) {
    name = 'Edge';
    const match = userAgent.match(/Edg\/(\d+)/);
    version = match ? parseInt(match[1], 10) : 0;
  }
  // Chrome
  else if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    name = 'Chrome';
    const match = userAgent.match(/Chrome\/(\d+)/);
    version = match ? parseInt(match[1], 10) : 0;
  }
  // Firefox
  else if (userAgent.includes('Firefox')) {
    name = 'Firefox';
    const match = userAgent.match(/Firefox\/(\d+)/);
    version = match ? parseInt(match[1], 10) : 0;
  }
  // Safari
  else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    name = 'Safari';
    const match = userAgent.match(/Version\/(\d+)/);
    version = match ? parseInt(match[1], 10) : 0;
  }

  const minVersion = SUPPORTED_BROWSERS[name];
  const isSupported = minVersion !== undefined && version >= minVersion;

  // Edge on macOS has known issues with Web Speech API
  const isEdgeMacOS = name === 'Edge' && isMacOS;

  return { name, version, isSupported, isMacOS, isEdgeMacOS };
};

export const BrowserSupportIndicator: React.FC = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [activePolicy, setActivePolicy] = useState<LegalPolicyId | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const browserInfo = useMemo(() => detectBrowser(), []);
  const { name, version, isSupported, isEdgeMacOS } = browserInfo;

  const showUnsupported = ALWAYS_SHOW_UNSUPPORTED || !isSupported || name === 'Brave';
  const currentBrowserLabel = `${name} ${version > 0 ? `v${version}` : ''}`.trim();

  const pillColorClass = isEdgeMacOS ? 'yellow' : 'green';

  const closeLegalModal = useCallback(() => setActivePolicy(null), []);

  useEffect(() => {
    if (!activePolicy) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const t = window.setTimeout(() => closeButtonRef.current?.focus(), 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLegalModal();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [activePolicy, closeLegalModal]);

  const activeDoc = activePolicy ? LEGAL_DOCUMENTS[activePolicy] : null;

  const legalModal =
    activeDoc &&
    createPortal(
      <div
        className="legal-modal-backdrop"
        role="presentation"
        onClick={closeLegalModal}
      >
        <div
          className="legal-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="legal-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="legal-modal-header">
            <h2 id="legal-modal-title" className="legal-modal-title">
              {activeDoc.title}
            </h2>
            <button
              ref={closeButtonRef}
              type="button"
              className="legal-modal-close"
              onClick={closeLegalModal}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="legal-modal-body">
            <ReactMarkdown>{activeDoc.markdown}</ReactMarkdown>
          </div>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <div
        className="browser-indicator-container"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className={`browser-indicator-pill ${isSupported && !showUnsupported ? 'supported' : 'supported'}`}
        >
          <span className={`browser-indicator-dot ${pillColorClass}`} />
          <span className="browser-indicator-text">{currentBrowserLabel}</span>
        </div>

        {isHovered && (
          <div className="browser-indicator-tooltip">
            <div className="tooltip-header">
              {isEdgeMacOS ? (
                <>
                  <span className="status-icon warning">⚠</span>
                  <span>Limited speech recognition support</span>
                </>
              ) : isSupported ? (
                <>
                  <span className="status-icon supported">✓</span>
                  <span>Your browser is supported</span>
                </>
              ) : (
                <>
                  <span className="status-icon unsupported">!</span>
                  <span>Browser not fully supported</span>
                </>
              )}
            </div>

            {isEdgeMacOS && (
              <>
                <div className="tooltip-divider" />
                <div className="tooltip-edge-warning">
                  <p>
                    Edge on macOS has limited speech recognition support. Your voice will be transcribed
                    using our server instead of browser-based recognition.
                  </p>
                  <p className="tooltip-edge-note">This works well but may have slightly higher latency.</p>
                </div>
              </>
            )}

            <div className="tooltip-divider" />
            <div className="tooltip-section">
              <span className="tooltip-label">Supported Browsers:</span>
              <ul className="browser-list">
                {Object.entries(SUPPORTED_BROWSERS).map(([browserName, minVersion]) => (
                  <li
                    key={browserName}
                    className={browserName === name ? 'current-browser' : ''}
                  >
                    {browserName} {minVersion}+
                    {browserName === name && ' (current)'}
                  </li>
                ))}
              </ul>
            </div>
            {(isEdgeMacOS || !isSupported) && (
              <div className="tooltip-recommendation">
                <strong>Recommendation:</strong> For the best experience on macOS, use{' '}
                <span className="recommended-browser">Chrome</span> or{' '}
                <span className="recommended-browser">Safari</span>.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="kouvent-branding">
        by <span className="kouvent-name">KOUVENT TECHNOLOGIES PVT LTD.</span>
      </div>

      <div className="login-legal-corner">
        {isEdgeMacOS && (
          <div className="edge-macos-notice edge-macos-notice--stacked">
            Using server transcription
          </div>
        )}
        <div className="legal-triggers" role="navigation" aria-label="Legal policies">
          <button
            type="button"
            className="legal-trigger"
            onClick={() => setActivePolicy('privacy')}
          >
            {LEGAL_DOCUMENTS.privacy.title}
          </button>
          <span className="legal-triggers-sep" aria-hidden>
            ·
          </span>
          <button
            type="button"
            className="legal-trigger"
            onClick={() => setActivePolicy('terms')}
          >
            {LEGAL_DOCUMENTS.terms.title}
          </button>
          <span className="legal-triggers-sep" aria-hidden>
            ·
          </span>
          <button
            type="button"
            className="legal-trigger"
            onClick={() => setActivePolicy('cookies')}
          >
            {LEGAL_DOCUMENTS.cookies.title}
          </button>
        </div>
      </div>

      {legalModal}
    </>
  );
};

export default BrowserSupportIndicator;
